// Staff stamp confirmation overlay. Visual chrome shared with the owner
// overlay (see shared/stamp/StampOverlayChrome). Same wait-for-server
// pattern as the owner — the slide locks-right + shows a spinner until
// the server confirms.

import { useEffect, useRef, useState } from 'react'
import {
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query'
import { toast } from 'sonner'
import { newId } from '@naya/shared'

import { useStaffSession } from '../hooks/useStaffSession.js'
import { ownerApi, OwnerApiError } from '../../owner/ownerApi.js'
import { errorMessageFor } from '../../owner/errorMessages.js'
import { playStampSuccessChime } from '../../shared/sound/stampSound.js'
import {
  RedemptionConfirmModal,
  type Milestone as RedeemableMilestone,
  type SecretMenuContext,
} from '../../shared/redemption/RedemptionUI.js'
import {
  RewardsPickerModal,
  stampToastMessage,
  StampOverlayBlocked,
  StampOverlayBody,
  StampOverlayShell,
  StampOverlaySkeleton,
  type StampOverlayReferralContext,
} from '../../shared/stamp/StampOverlayChrome.js'
import { MembershipScanController } from '../../shared/membership/MembershipScanController.js'
import { EventTicketScanController } from './EventTicketScanController.js'
import type { MembershipContext } from '../../shared/membership/membershipTypes.js'

/**
 * How the card being stamped was identified. A QR scan carries the signed
 * `stamp_token`; a code/email/phone lookup (the no-QR fallback) carries only
 * the resolved cardId. The two take different stamp endpoints:
 *   - 'scanned' → POST /api/staff/cards/:id/stamps  (token-verified)
 *   - 'lookup'  → POST /api/cards/:id/stamps         (owner route, role-gated;
 *                  accepts 'staff', needs no token — see CLAUDE.md 4a-4)
 */
export type StaffStampSource =
  | { kind: 'scanned'; cardId: string; rawToken: string }
  | { kind: 'lookup'; cardId: string }

interface StaffTokenStampResponse {
  stamps_count: number
  total_stamps: number
  milestones_just_crossed: Array<{
    at_stamp: number
    reward_text: string
    type: 'intermediate' | 'final'
  }>
  card_id: string
  requested_count: number
  stamps_added: number
  secret_stamps_added: number
  replayed?: boolean
}

// The owner stamp route (used by the 'lookup' path) returns a richer shape.
interface OwnerStampResponse {
  card: { id: string; stamps_count: number; tenant_id: string }
  newly_earned_milestones: Array<{
    at_stamp: number
    reward_text: string
    type: 'intermediate' | 'final'
  }>
  requested_count: number
  stamps_added: number
  secret_stamps_added: number
  replayed?: boolean
}

// Both endpoints normalised to what the overlay actually reads.
interface NormalisedStamp {
  stamps_count: number
  milestones_just_crossed: Array<{
    at_stamp: number
    reward_text: string
    type: 'intermediate' | 'final'
  }>
  requested_count: number
  stamps_added: number
  secret_stamps_added: number
  // The server matched this scan to a stamp it already recorded and added
  // nothing — see IssueStampResult.replayed on the API.
  replayed: boolean
}

interface StaffCardDetail {
  card: {
    stamps_count: number
    card_code: string
    program_snapshot: { total_stamps: number; name?: string }
    pass_type?: 'loyalty' | 'membership' | 'event_ticket'
  }
  // Customer lifecycle (2026-09-05) — deactivated_at/erased_at drive the
  // first-paint revoked/erased block below (see StampOverlayBlocked).
  customer: {
    first_name: string | null
    deactivated_at: string | null
    deactivation_reason: string | null
    erased_at: string | null
  } | null
  pending_milestones: RedeemableMilestone[]
  referral_context?: StampOverlayReferralContext | null
  // SLICE-MEMBERSHIP-PASS — null for loyalty cards; present on membership.
  membership_context?: MembershipContext | null
  // Secret menu — see SecretMenuContext doc comment. Null unless this card
  // has a pending FINAL milestone right now.
  secret_menu_context?: SecretMenuContext | null
  // Whether off-menu tagging is live for this card's tenant/program (env +
  // entitlement + owner toggle). Gates the off-menu stepper.
  secret_menu_adjustable?: { enabled: boolean }
}

interface StaffProgramsResponse {
  programs: Array<{ id: string; name: string; is_default: boolean }>
}

export function StaffStampOverlay({
  source,
  onDone,
}: {
  source: StaffStampSource
  onDone: () => void
}) {
  const { data: me } = useStaffSession()
  const queryClient = useQueryClient()
  const branchId = me?.user.branch_id ?? null
  // Multi-stamp selector — how many stamps the next slide adds. Reset to 1
  // after every successful stamp so a leftover count never silently carries
  // over to the next customer.
  const [count, setCount] = useState(1)
  // Secret menu — how many of `count` stamps in this action are off-menu.
  // Clamped to never exceed `count`; reset to 0 alongside `count` after
  // every successful stamp (see stampMutation.onSuccess below).
  const [secretCount, setSecretCount] = useState(0)
  useEffect(() => {
    setSecretCount((s) => Math.min(s, count))
  }, [count])
  const [earned, setEarned] = useState<RedeemableMilestone[]>([])
  const [redeemTarget, setRedeemTarget] = useState<RedeemableMilestone | null>(
    null,
  )
  // True when `earned` was populated by a stamp mutation we just ran (not
  // by the pre-stamp first-paint auto-open). Controls what "Skip for now"
  // does: post-stamp skip ends the session (onDone); pre-stamp skip
  // returns the user to the slide so they can stamp this visit.
  const earnedFromStampRef = useRef(false)
  // Multi-stamp selector — the just-completed mutation's requested/added
  // counts, so the "Skip for now" toast (fired after the picker, once the
  // mutation's own onSuccess closure has already exited) can still report
  // an accurate "Added 4 of 5 stamps" message.
  const lastStampResultRef = useRef({
    requested_count: 1,
    stamps_added: 1,
    secret_stamps_added: 0,
  })
  // True once any reward has been redeemed in this overlay session. After
  // a redeem, the overlay nudges toward Done — the slide stays available
  // for the combined buy+redeem case but loses primary emphasis.
  const [redeemedThisSession, setRedeemedThisSession] = useState(false)
  // SLICE-MEMBERSHIP-PASS — flips true when the stamp endpoint returns 409
  // MEMBERSHIP_CARD (safety net if the first-paint detail check didn't catch
  // it). Switches this overlay to the read-only verify/check-in panel.
  const [membershipForced, setMembershipForced] = useState(false)

  const detailKey = ['staff', 'card-detail', source.cardId] as const
  const detail = useQuery({
    queryKey: detailKey,
    queryFn: () =>
      ownerApi.get<StaffCardDetail>(
        `/cards/${encodeURIComponent(source.cardId)}`,
      ),
  })
  const data = detail.data
  const customerName = data?.customer?.first_name?.trim() || 'Customer'
  const cardCode =
    data?.card.card_code ?? `Card ${source.cardId.slice(-6).toUpperCase()}`

  // Show the program-name pill only when this tenant runs more than one
  // program — otherwise it's redundant. Cheap, cached lookup.
  const programsQ = useQuery({
    queryKey: ['staff', 'programs'],
    queryFn: () => ownerApi.get<StaffProgramsResponse>('/staff/programs'),
    staleTime: 60_000,
  })
  const multiProgram = (programsQ.data?.programs ?? []).length > 1

  const pickerDismissedRef = useRef(false)

  // Idempotency key for the 'lookup' stamp path (the owner route requires
  // one; the token route derives idempotency from the signed token itself).
  // Re-minted after each successful stamp so a combined buy+redeem visit can
  // stamp again.
  const idempotencyKeyRef = useRef<string>(newId())

  const stampMutation = useMutation({
    mutationFn: async (): Promise<NormalisedStamp> => {
      if (source.kind === 'scanned') {
        const res = await ownerApi.post<StaffTokenStampResponse>(
          `/staff/cards/${encodeURIComponent(source.cardId)}/stamps`,
          { stamp_token: source.rawToken, count, secret_count: secretCount },
        )
        return {
          stamps_count: res.stamps_count,
          milestones_just_crossed: res.milestones_just_crossed,
          requested_count: res.requested_count,
          stamps_added: res.stamps_added,
          secret_stamps_added: res.secret_stamps_added,
          replayed: res.replayed ?? false,
        }
      }
      // 'lookup' — no scanned token; stamp via the owner route, which
      // accepts the staff role and needs only branch + idempotency key.
      if (!branchId) throw new Error('No branch assigned to this staff account')
      const res = await ownerApi.post<OwnerStampResponse>(
        `/cards/${encodeURIComponent(source.cardId)}/stamps`,
        {
          branch_id: branchId,
          idempotency_key: idempotencyKeyRef.current,
          count,
          secret_count: secretCount,
        },
      )
      idempotencyKeyRef.current = newId()
      return {
        stamps_count: res.card.stamps_count,
        milestones_just_crossed: res.newly_earned_milestones,
        requested_count: res.requested_count,
        stamps_added: res.stamps_added,
        secret_stamps_added: res.secret_stamps_added,
        replayed: res.replayed ?? false,
      }
    },
    onSuccess: (result) => {
      if (result.replayed) {
        // Nothing was added: the pass the customer showed is an out-of-date
        // copy whose barcode was already used. Say so plainly (no success
        // chime/toast) and tell staff exactly what to do next.
        void queryClient.invalidateQueries({ queryKey: detailKey })
        const total = data?.card.program_snapshot.total_stamps
        toast.warning('No stamp was added', {
          description:
            `${customerName}'s pass is out of date${
              total ? ` (still ${result.stamps_count} / ${total})` : ''
            }. We just sent their phone an update. Ask them to open their wallet app ` +
            `and pull down to refresh the pass, then scan again. ` +
            `Or tap "Can't scan?" to stamp them by name or card code.`,
          duration: 15000,
        })
        onDone()
        return
      }
      playStampSuccessChime()
      setCount(1)
      setSecretCount(0)
      lastStampResultRef.current = {
        requested_count: result.requested_count,
        stamps_added: result.stamps_added,
        secret_stamps_added: result.secret_stamps_added,
      }
      // Optimistically merge the server response into cache so the
      // displayed n/total + pending list update from the mutation result
      // — not from a refetch on the critical path. The invalidate below
      // is fire-and-forget; whenever it lands the server state overwrites
      // this merge.
      queryClient.setQueryData<StaffCardDetail>(detailKey, (prev) =>
        prev
          ? {
              ...prev,
              card: { ...prev.card, stamps_count: result.stamps_count },
              pending_milestones: mergePending(
                prev.pending_milestones,
                result.milestones_just_crossed,
              ),
            }
          : prev,
      )
      void queryClient.invalidateQueries({ queryKey: detailKey })

      if (result.milestones_just_crossed.length > 0) {
        pickerDismissedRef.current = false
        earnedFromStampRef.current = true
        const cached = queryClient.getQueryData<StaffCardDetail>(detailKey)
        const allPending = mergePending(
          cached?.pending_milestones ?? [],
          result.milestones_just_crossed,
        )
        setEarned(allPending)
      } else {
        const totalStamps = data?.card.program_snapshot.total_stamps
        toast.success(
          totalStamps
            ? stampToastMessage(customerName, result.stamps_count, totalStamps, result)
            : `Stamp added · ${customerName} · ${result.stamps_count}`,
        )
        onDone()
      }
    },
    onError: (err) => {
      // SLICE-MEMBERSHIP-PASS — a membership card can't be stamped: the server
      // returns 409 MEMBERSHIP_CARD. Switch to the verify/check-in panel
      // instead of toasting a stamp error. Only the scanned path carries a
      // token, so only it can verify; the no-token lookup path falls through.
      if (
        err instanceof OwnerApiError &&
        err.code === 'MEMBERSHIP_CARD' &&
        source.kind === 'scanned'
      ) {
        setMembershipForced(true)
        return
      }
      const message =
        err instanceof OwnerApiError && err.code
          ? errorMessageFor(err.code)
          : err instanceof OwnerApiError
            ? err.message
            : 'Stamp failed'
      toast.error(message)
    },
  })

  function fireStamp() {
    if (!data || stampMutation.isPending) return
    stampMutation.mutate()
  }

  // SLICE-MEMBERSHIP-PASS — first-paint detection: the card detail tells us
  // it's a membership card before staff even slides, so we can show the verify
  // panel straight away. The 409 catch above is the safety net. Only the
  // scanned source carries the token needed to check in.
  const isMembership =
    source.kind === 'scanned' &&
    (membershipForced ||
      data?.card.pass_type === 'membership' ||
      data?.membership_context != null)

  if (isMembership && source.kind === 'scanned') {
    return (
      <MembershipScanController
        cardId={source.cardId}
        rawToken={source.rawToken}
        endpoint="staff"
        onDone={onDone}
      />
    )
  }

  // SLICE-EVENT-TICKET — a scanned event ticket goes straight to the gate
  // check-in (active → used), not the stamp slide. First-paint detection on the
  // card's pass_type (the detail GET returns it); only the scanned source
  // carries the token needed to check in.
  if (
    source.kind === 'scanned' &&
    data?.card.pass_type === 'event_ticket'
  ) {
    return (
      <EventTicketScanController
        cardId={source.cardId}
        rawToken={source.rawToken}
        onDone={onDone}
      />
    )
  }

  // First-paint auto-open: if the loaded card already carries pending
  // milestones from a prior visit (customer skipped their reward), surface
  // the picker as the primary action. Render-time check rather than a
  // useEffect so a background refetch landing mid-session can't toggle
  // the picker closed-then-open.
  const showPickerFromData =
    !!data &&
    data.pending_milestones.length > 0 &&
    earned.length === 0 &&
    !redeemTarget &&
    !pickerDismissedRef.current &&
    !stampMutation.isPending
  const pickerMilestones = earned.length > 0
    ? earned
    : showPickerFromData
      ? data!.pending_milestones
      : []

  // Customer lifecycle (2026-09-05) — block before the slide ever renders,
  // rather than letting staff slide into a generic 423 toast.
  const blockedKind: 'erased' | 'revoked' | null = data?.customer?.erased_at
    ? 'erased'
    : data?.customer?.deactivated_at
      ? 'revoked'
      : null

  return (
    <StampOverlayShell>
      {!data ? (
        <StampOverlaySkeleton onCancel={onDone} />
      ) : blockedKind ? (
        <StampOverlayBlocked
          customerName={customerName}
          kind={blockedKind}
          at={
            blockedKind === 'erased'
              ? data.customer!.erased_at
              : data.customer!.deactivated_at
          }
          reason={data.customer?.deactivation_reason}
          onCancel={onDone}
        />
      ) : (
        <StampOverlayBody
          data={data}
          customerName={customerName}
          cardCode={cardCode}
          programName={
            multiProgram ? data.card.program_snapshot.name : undefined
          }
          referral={data.referral_context ?? null}
          onCancel={onDone}
          onSlideConfirm={fireStamp}
          onClaimReward={() => setEarned(data.pending_milestones)}
          slidePending={stampMutation.isPending}
          pickerOpen={pickerMilestones.length > 0 || redeemTarget !== null}
          postRedeem={redeemedThisSession}
          count={count}
          onCountChange={setCount}
          secretCount={secretCount}
          onSecretCountChange={
            data.secret_menu_adjustable?.enabled ? setSecretCount : undefined
          }
        />
      )}

      {pickerMilestones.length > 0 && !redeemTarget && data && (
        <RewardsPickerModal
          name={customerName}
          milestones={pickerMilestones}
          onRedeem={(m) => setRedeemTarget(m)}
          secretMenuContext={data.secret_menu_context ?? null}
          onSkip={() => {
            // Post-stamp skip ends the session — staff just stamped, the
            // customer chose not to claim; don't bait another stamp.
            // Pre-stamp skip (from the first-paint auto-open) returns to
            // the slide so this visit's stamp can still be added.
            if (earnedFromStampRef.current) {
              earnedFromStampRef.current = false
              setEarned([])
              toast.success(
                stampToastMessage(
                  customerName,
                  data!.card.stamps_count,
                  data!.card.program_snapshot.total_stamps,
                  lastStampResultRef.current,
                ),
              )
              onDone()
              return
            }
            pickerDismissedRef.current = true
            setEarned([])
          }}
        />
      )}

      {redeemTarget && (
        <RedemptionConfirmModal
          milestone={redeemTarget}
          cardId={source.cardId}
          branchId={branchId}
          secretMenuContext={data?.secret_menu_context ?? null}
          postRedemption={(cardId, body) =>
            ownerApi.post(
              `/cards/${encodeURIComponent(cardId)}/redemptions`,
              body,
            )
          }
          errorMessageFor={errorMessageFor}
          onClose={() => setRedeemTarget(null)}
          onRedeemed={(m) => {
            setEarned((prev) => prev.filter((x) => x.at_stamp !== m.at_stamp))
            queryClient.setQueryData<StaffCardDetail>(detailKey, (prev) =>
              prev
                ? {
                    ...prev,
                    pending_milestones: prev.pending_milestones.filter(
                      (x) => x.at_stamp !== m.at_stamp,
                    ),
                  }
                : prev,
            )
            // After a redemption, nudge staff toward closing rather than
            // stamping. The slide stays available (combined buy + redeem
            // is a real flow) but the secondary action becomes a primary-
            // styled "Done".
            setRedeemedThisSession(true)
          }}
          onCommitted={() => {
            void queryClient.invalidateQueries({ queryKey: detailKey })
          }}
        />
      )}
    </StampOverlayShell>
  )
}

function mergePending(
  a: RedeemableMilestone[],
  b: RedeemableMilestone[],
): RedeemableMilestone[] {
  const map = new Map<number, RedeemableMilestone>()
  for (const m of a) map.set(m.at_stamp, m)
  for (const m of b) map.set(m.at_stamp, m)
  return Array.from(map.values()).sort((x, y) => x.at_stamp - y.at_stamp)
}
